import React, { useCallback, useState } from 'react';
import { Check, Loader2, Signal, TriangleAlert } from 'lucide-react';
import { useSimulation } from '../context/SimulationContext';

const TROUBLE_MSG =
  "Windows could not automatically detect this network's proxy settings";

export function WifiFlyout({
  onClose,
}: {
  onClose: () => void;
}): React.ReactElement {
  const { openWindow, logAction, state } = useSimulation();
  const { isFixed } = state;
  const [troubleLoading, setTroubleLoading] = useState(false);
  const [troubleDone, setTroubleDone] = useState(false);

  const runTroubleshooter = useCallback(() => {
    setTroubleDone(false);
    setTroubleLoading(true);
    logAction('ran_troubleshooter');
    window.setTimeout(() => {
      setTroubleLoading(false);
      setTroubleDone(true);
    }, 3000);
  }, [logAction]);

  const openNet = useCallback(() => {
    openWindow('network-settings');
    onClose();
  }, [onClose, openWindow]);

  return (
    <div
      className="absolute bottom-14 right-3 z-[5000] w-[320px] rounded-xl border border-white/20 bg-white/10 backdrop-blur-xl shadow-2xl shadow-black/50 animate-fade-in"
      role="dialog"
      aria-label="Wi-Fi quick settings"
    >
      <div className="border-b border-white/10 px-4 py-3">
        <div className="flex items-start gap-3">
          <div
            className={`flex flex-col items-center gap-1 ${isFixed ? 'text-emerald-400' : 'text-white/35'}`}
          >
            <Signal className="h-10 w-10" aria-hidden strokeWidth={1.5} />
            <span className="flex gap-0.5" aria-hidden>
              {[3, 5, 7, 9].map((h) => (
                <span
                  key={h}
                  className={`w-1 rounded-sm ${isFixed ? 'bg-emerald-400/90' : 'bg-white/25'}`}
                  style={{ height: h * 2 }}
                />
              ))}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-white font-medium">
              <span className="truncate">Office-WiFi</span>
              {isFixed ? (
                <Check
                  className="h-4 w-4 shrink-0 text-emerald-400"
                  aria-hidden
                  strokeWidth={2.5}
                />
              ) : (
                <TriangleAlert
                  className="h-4 w-4 shrink-0 text-amber-400"
                  aria-hidden
                />
              )}
            </div>
            <p className="mt-1 text-xs text-white/70">
              {isFixed ? 'Connected, secured' : 'No internet access'}
            </p>
          </div>
        </div>
      </div>
      <div className="p-3 flex flex-col gap-2">
        <button
          type="button"
          className="w-full rounded-lg bg-white/10 px-3 py-2 text-left text-sm text-white hover:bg-white/15 transition-colors border border-white/15"
          onClick={openNet}
        >
          Open Network &amp; Internet Settings
        </button>
        {!isFixed && (
          <button
            type="button"
            className="w-full rounded-lg bg-white/10 px-3 py-2 text-left text-sm text-white hover:bg-white/15 transition-colors border border-white/15 disabled:opacity-60"
            onClick={runTroubleshooter}
            disabled={troubleLoading}
          >
            {troubleLoading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Detecting problems…
              </span>
            ) : (
              'Troubleshoot problems'
            )}
          </button>
        )}
        {troubleDone && !isFixed && (
          <p className="text-xs text-amber-200/90 px-1 leading-relaxed animate-fade-in">
            {TROUBLE_MSG}
          </p>
        )}
      </div>
    </div>
  );
}
